import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

// Phase 2 (3/n) — LinkedCursor.
//
// Pure-logic controller (no uPlot dep). Holds a shared "current x"
// state across multiple chart panes so a click on the trend track
// drives the spectrogram + spectrum + waveform views to the same
// timestamp. Phase 4 S5 Signal Analysis is the primary consumer.
//
// The `[` and `]` keyboard shortcuts (registered via the existing
// useKeyboardMap) step the cursor forward / backward by the supplied
// tickSeconds. The shortcut sheet documents them as
// "LinkedCursor back / forward one tick".

export type LinkedCursorState = {
  /** Unix epoch in seconds, or null when no point is pinned. */
  t: number | null;
  /** True while the cursor is being dragged. Charts can suppress
   *  expensive recompute on every move and snap on mouseup. */
  dragging: boolean;
};

export type LinkedCursorApi = {
  state: LinkedCursorState;
  /** Set absolute time. Null clears the cursor. */
  setT: (t: number | null) => void;
  /** Mark drag start / end. UI can pin at end-of-drag. */
  setDragging: (dragging: boolean) => void;
  /** Step the cursor by the configured tickSeconds; no-op when t is null. */
  step: (direction: 1 | -1) => void;
  /** The configured per-tick step size (seconds). Surfaced so consumers
   *  can render "step ±1m" labels without re-stating the constant. */
  tickSeconds: number;
};

const LinkedCursorContext = createContext<LinkedCursorApi | null>(null);

export type LinkedCursorProviderProps = {
  /** Default seconds per `[`/`]` step. Defaults to 60s. */
  tickSeconds?: number;
  /** Initial cursor position. Null = unpinned. */
  initialT?: number | null;
  children: ReactNode;
};

export function LinkedCursorProvider({
  tickSeconds = 60,
  initialT = null,
  children,
}: LinkedCursorProviderProps) {
  const [t, setTState] = useState<number | null>(initialT);
  const [dragging, setDraggingState] = useState(false);

  const setT = useCallback((next: number | null) => {
    setTState(next);
  }, []);

  const setDragging = useCallback((next: boolean) => {
    setDraggingState(next);
  }, []);

  const step = useCallback(
    (direction: 1 | -1) => {
      setTState((current) => {
        if (current == null) return current;
        return current + direction * tickSeconds;
      });
    },
    [tickSeconds],
  );

  const api = useMemo<LinkedCursorApi>(
    () => ({
      state: { t, dragging },
      setT,
      setDragging,
      step,
      tickSeconds,
    }),
    [t, dragging, setT, setDragging, step, tickSeconds],
  );

  return (
    <LinkedCursorContext.Provider value={api}>
      {children}
    </LinkedCursorContext.Provider>
  );
}

/**
 * useLinkedCursor returns the active LinkedCursor api. Throws when
 * called outside a provider — same pattern as ThemeProvider, so
 * mistaken usage fails loud during development.
 */
export function useLinkedCursor(): LinkedCursorApi {
  const ctx = useContext(LinkedCursorContext);
  if (!ctx) {
    throw new Error('useLinkedCursor must be used inside <LinkedCursorProvider>');
  }
  return ctx;
}

/**
 * useOptionalLinkedCursor returns the api when a provider is present,
 * or null when there isn't one. Used by chart components that want to
 * participate in the linked cursor when wrapped in a provider but
 * still render correctly in isolation (e.g. Storybook).
 */
export function useOptionalLinkedCursor(): LinkedCursorApi | null {
  return useContext(LinkedCursorContext);
}
